/**
 * Barla — gezi rehberi.
 *
 * Metin ve fotoğraflar Nur Menzilleri'nin (İstanbul İlim ve Kültür
 * Vakfı) Barla sayfasından alındı; sayfanın altında kaynak yazılı.
 *
 * Fotoğraflar kaynakta adlandırılmamış. Hangi yapı olduğu kesin
 * olmayanlara isim uydurmuyoruz: bölümün altında iddiasız duruyorlar.
 * Yalnız üzerindeki yazıdan ya da meşhur görüntüsünden tanınanlar
 * (vakfiye levhası, Cennet Bahçesi tabelası, çınardaki sayvan)
 * adıyla anılıyor.
 */
import { Phone, ExternalLink, MapPin } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui';

const KOK = '/gorsel/barla';

function Foto({ ad, alt, genis }: { ad: string; alt: string; genis?: boolean }) {
  return (
    <figure className={genis ? 'col-span-2' : ''}>
      <img
        src={`${KOK}/${ad}.webp`}
        alt={alt}
        loading="lazy"
        decoding="async"
        className="w-full rounded-lg border border-border object-cover shadow-sm"
      />
      <figcaption className="mt-1 text-[11px] leading-snug text-muted-foreground">{alt}</figcaption>
    </figure>
  );
}

function Bolum({
  baslik,
  children,
  fotograflar,
}: {
  baslik: string;
  children: React.ReactNode;
  fotograflar?: { ad: string; alt: string; genis?: boolean }[];
}) {
  return (
    <Card className="space-y-3">
      <h3 className="font-serif text-lg font-semibold text-primary">{baslik}</h3>
      <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">{children}</div>
      {fotograflar && fotograflar.length > 0 && (
        <div className="grid grid-cols-2 gap-2 pt-1">
          {fotograflar.map((f) => (
            <Foto key={f.ad} {...f} />
          ))}
        </div>
      )}
    </Card>
  );
}

function Alinti({ children, kaynak }: { children: React.ReactNode; kaynak: string }) {
  return (
    <blockquote className="border-l-2 border-accent/60 bg-accent/5 py-2 pl-3 pr-2">
      <p className="font-serif text-sm italic leading-relaxed text-primary">{children}</p>
      <cite className="mt-1 block text-[11px] not-italic text-muted-foreground">{kaynak}</cite>
    </blockquote>
  );
}

export function BarlaPage() {
  return (
    <div className="space-y-4 pb-4">
      <PageHeader title="Barla" subtitle="Risale-i Nur'un telif edildiği kasaba" backTo="/" />

      <Card className="space-y-3">
        <img
          src={`${KOK}/harita.webp`}
          alt="Barla'nın Isparta ve Eğirdir'e göre yeri"
          loading="lazy"
          decoding="async"
          className="w-full rounded-lg border border-border"
        />
        <p className="text-sm leading-relaxed text-muted-foreground">
          Barla, Isparta'nın Eğirdir ilçesine bağlı, Eğirdir Gölü'nün batısında bir kasabadır.
          Isparta'ya 40, Eğirdir'e 22, Antalya'ya 170 kilometredir.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Üç bin nüfuslu bu kasabanın tarihi çok eskiye gider; fakat Barla, 1926–1934 yılları
          arasında Bediüzzaman Said Nursî'yi misafir etmesiyle tanınmıştır. Risale-i Nur Külliyatı
          tanındıkça Barla da tanınmış, kasaba Bediüzzaman ve Risale-i Nur ile özdeşleşmiştir.
        </p>
      </Card>

      <Card className="space-y-2">
        <h3 className="font-serif text-lg font-semibold text-primary">Barla'da gezilecek yerler</h3>
        <ol className="space-y-1.5 text-sm leading-relaxed text-muted-foreground">
          {[
            'Bediüzzaman’ın 1926–1934 yılları arasında kaldığı ev',
            'Yirmi Sekizinci Söz’ün yazıldığı Sıddık Süleyman Bey’in bahçesi — bugünkü adıyla Cennet Bahçesi',
            'Bediüzzaman’ın imamlık yaptığı Muş Mescidi',
            'Hacı Hâfız Mustafa Öztürk ve Hacı Mehmet Tevfik Öztürk Kültür Evi',
            'Bediüzzaman’ın tefekkür mekânı Çam Dağı',
          ].map((s, i) => (
            <li key={s} className="flex gap-2">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent/15 text-[11px] font-semibold text-accent">
                {i + 1}
              </span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      </Card>

      <Bolum
        baslik="Bediüzzaman’ın kaldığı ev"
        fotograflar={[
          { ad: 'ev-sayvan', alt: 'Evin önündeki çınar ve ağaçtaki sayvan' },
          { ad: 'ev-cinar', alt: 'Çınar ağacındaki sayvan' },
          { ad: 'vakfiye-levha', alt: 'Evin duvarındaki vakfiye levhası (Nisan 1954)' },
          { ad: 'barla-manzara', alt: 'Barla’dan Eğirdir Gölü’ne bakış' },
        ]}
      >
        <p>
          Sürgünde göz altında tutulduğu eve varıldığında iki katlı, iki odalı mütevazı bir köy
          odası karşılar. Evin önünde bir çınar, ağacın üzerinde Said Nursî’nin zaman zaman çıkıp
          eserlerini yazdığı sayvan vardır. Çınarın hemen yanındaki çeşme yanık yürekleri
          ferahlatır.
        </p>
        <p>
          Yıllarca Bediüzzaman’ın evradına, ibadetine, dualarına ve ıstırabına şahitlik eden çınar,
          evin tam karşısındadır.
        </p>
        <Alinti kaynak="Son Şahitler, 1. cilt, s. 409">
          “Ehl-i hükûmet gelerek, ‘Eğer razı olursan şu ağacın bir dalını keseceğiz, sana da on bin
          altın vereceğiz; bu parayı Risâle-i Nur’un hizmetine sarf edersin’ deseler, vallahi razı
          olmam.”
        </Alinti>
        <p>
          Talebeleri, Bediüzzaman’ın 1953’te yaklaşık yirmi yıllık bir ayrılıktan sonra Barla’ya
          tekrar ayak bastığında çınara sarıldığını ve uzun uzun ağladığını anlatır.
        </p>
        <p>
          Eve girildiğinde duvardaki levhada şu satırlar okunur: “Altı vilâyet genişliğindeki
          mânevî Medresetü’z-Zehra’nın çekirdeği ve birinci meyvesi olan ve sekiz sene ikamet
          ettiğim ve Risale-i Nur’un telif merkezi olan bu evimi bir Medrese-i Nuriye olarak
          vakfettim.”
        </p>
      </Bolum>

      <Bolum
        baslik="Cennet Bahçesi"
        fotograflar={[
          { ad: 'cennet-levha', alt: 'Cennet Bahçesi’nin girişindeki levha' },
          { ad: 'bahce-giris', alt: 'Bahçenin girişi' },
          { ad: 'cennet-yol', alt: 'Bahçe içindeki yol' },
          { ad: 'cennet-merdiven', alt: 'Bahçeye inen merdiven' },
        ]}
      >
        <p>
          Bediüzzaman’ın sık sık uğradığı, evrad ve ezkâr okuyarak tefekküre daldığı yerlerdendir.
          Nur talebelerinden Sıddık Süleyman’ın annesi Şefika Hanım’ın bağışladığı bahçe, adını
          burada yazılan bir risaleden alır: cennete dair bahislerin yer aldığı Yirmi Sekizinci Söz
          burada kaleme alınmıştır.
        </p>
        <p>
          O gün bir üzüm bağı ve birkaç ağaçtan ibaret olan alan, talebeleri tarafından Türkiye’nin
          dört bir yanından getirilen ağaçlarla donatılarak ismine lâyık hâle getirilmiştir. Uzunca
          bir merdivenden inildikten sonra yemyeşil ağaçların arasında gezinilebilir.
        </p>
      </Bolum>

      <Bolum baslik="Muş Mescidi">
        <p>
          Aslına uygun olarak restore edilen mescidin Bediüzzaman’ın hayatında önemli bir yeri
          vardır. İlk inşa tarihi belli değildir; metruk ve harap hâline üzülen Üstad, talebeleriyle
          birlikte tamirini üstlenmiş ve dört yıl boyunca burada fahrî imamlık yapmıştır.
        </p>
      </Bolum>

      <Bolum
        baslik="Hacı Mehmet Tevfik Kültür Evi"
        fotograflar={[
          { ad: 'yapi-1', alt: 'Barla’da restore edilmiş yapılardan biri' },
          { ad: 'ic-mekan', alt: 'Ahşap bir iç mekân' },
        ]}
      >
        <p>
          Bediüzzaman’ın 1953–1960 yılları arasında Barla’ya geldiğinde kaldığı ev olan “Hacı Hâfız
          Mustafa Öztürk ve Hacı Mehmet Tevfik Öztürk Kültür Evi”, Isparta İl Özel İdaresi
          tarafından restore edilerek 2013 yılında ziyarete açılmıştır.
        </p>
      </Bolum>

      <Bolum
        baslik="Çam Dağı"
        fotograflar={[
          { ad: 'camdagi-patika', alt: 'Çam Dağı’na çıkan patika' },
          { ad: 'camdagi-levha', alt: 'Çam Dağı’nda Bediüzzaman’ın hatırasına konan levha' },
          { ad: 'camdagi-cardak', alt: 'Çam Dağı’ndaki ahşap çardak' },
          { ad: 'camdagi-yapi', alt: 'Çam Dağı’nda bir yapı' },
          { ad: 'gol', alt: 'Yükseklerden Eğirdir Gölü', genis: true },
        ]}
      >
        <p>
          Bediüzzaman Çam Dağı’na sekiz buçuk sene gelip gitmiş, yazları üç ayı bu dağda geçirmiş;
          burada ve Barla’da ders, ibadet ve tefekkürle Risale-i Nur’u telif etmiştir.
        </p>
        <p>
          Eğirdir Gölü’nden yaklaşık on üç kilometre uzaklıktaki yolun on bir kilometresi taş
          döşelidir; iki kilometrelik kısım toprak yoldur ve sonrasında Çam Dağı’na yürüyerek
          tırmanmak gerekir.
        </p>
        <p>
          Sürgün 1926’da, kış mevsiminde başlar. Isparta’dan Barla’ya buzlar kırılarak, kayıklarla
          götürülür. Barla o gün sekiz on haneli, “kuş uçmaz kervan geçmez” denilen bir köydür. İlk
          günlerde çınar ağacına tefekkür için çıkar, dalları arasına küçük bir ders ve tefekkür
          mekânı yaptırır. Baharla birlikte gezileri artar; sonraları dört saat yürüyüş mesafesindeki
          Çam Dağı’na gidip gelmeye başlar.
        </p>
        <Alinti kaynak="Mektubat, Dördüncü Mektup">
          “Aziz kardeşlerim! Ben şimdi Çam Dağında yüksek bir tepede, büyük bir çam ağacının
          tepesinde, bir menzilde bulunuyorum. İnsten tevahhuş ve vuhuşa ünsiyet ettim…”
        </Alinti>
        <Alinti kaynak="Mektubat, Üçüncü Mektup">
          “Ben bu menzilleri Yıldız Sarayına değişmem.”
        </Alinti>
      </Bolum>

      <Card className="space-y-3 border-accent/40">
        <h3 className="font-serif text-lg font-semibold text-primary">Konaklama ve rezervasyon</h3>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Çam Dağı’nda konaklamak isterseniz İstanbul İlim ve Kültür Vakfı ile iletişime
          geçebilirsiniz. Nuriye Anne Medresesi için rezervasyon ve fiyat bilgisi de aynı
          numaradan alınıyor.
        </p>
        <div className="flex flex-col gap-2">
          <a
            href="tel:+905321743668"
            className="inline-flex items-center gap-2 rounded-lg border border-accent/40 bg-accent/10 px-3 py-2 text-sm font-medium text-primary"
          >
            <Phone className="h-4 w-4 text-accent" />
            0532 174 36 68
          </a>
          <a
            href="https://www.iikv.org"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-secondary hover:text-accent"
          >
            <ExternalLink className="h-4 w-4" />
            iikv.org — İstanbul İlim ve Kültür Vakfı
          </a>
          <a
            href="https://www.nurmenzilleri.com/nuriyeanne/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-secondary hover:text-accent"
          >
            <MapPin className="h-4 w-4" />
            Nuriye Anne Medresesi
          </a>
        </div>
      </Card>

      <p className="px-1 text-center text-[11px] leading-relaxed text-muted-foreground">
        Bu sayfadaki bilgiler ve fotoğraflar Nur Menzilleri’nin (İstanbul İlim ve Kültür Vakfı)
        Barla sayfasından derlenmiştir.{' '}
        <a
          href="https://www.nurmenzilleri.com/barla-2/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-accent underline"
        >
          nurmenzilleri.com
        </a>
      </p>
    </div>
  );
}
